import React from 'react';
import {
    Stack, TextField, RadioGroup, FormControlLabel, Radio, Typography,
    Select, MenuItem, FormGroup, Checkbox, InputAdornment, LinearProgress,
    Box, Divider, Autocomplete
} from '@mui/material';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { MobileDateTimePicker, MobileDatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import VoiceInput from './VoiceInput';
import { ACCENT, TEXT_PRIMARY, TEXT_SECONDARY, TEXT_TERTIARY, STATUS_LOST } from '../../designsystem/tokens';

const Properties = ({ properties, setProperties, onValidationChange }) => {
    const [errors, setErrors] = React.useState({});
    const [touchedFields, setTouchedFields] = React.useState(new Set());
    const { t } = useTranslation();

    const markTouched = (fieldName) => {
        setTouchedFields(prev => {
            const next = new Set(prev);
            next.add(fieldName);
            return next;
        });
    };

    const isMandatoryProp = (prop, allProps) => {
        if (prop.mandatory === true) return true;
        if (prop.mandatoryIf) {
            const dep = allProps.find(p => p.name === prop.mandatoryIf);
            if (dep && dep.value !== undefined && dep.value !== null && dep.value !== "") return true;
        }
        return false;
    };

    const validate = (currentProperties) => {
        const newErrors = {};
        let isValid = true;

        currentProperties.forEach((prop) => {
            const isMandatory = isMandatoryProp(prop, currentProperties);
            const isValueEmpty = prop.value === undefined || prop.value === null || prop.value === "" ||
                (Array.isArray(prop.value) && prop.value.length === 0);

            if (isMandatory && isValueEmpty) {
                newErrors[prop.name] = true;
                isValid = false;
            }
        });

        setErrors(newErrors);
        if (onValidationChange) {
            onValidationChange(isValid);
        }
        return isValid;
    };

    React.useEffect(() => {
        validate(properties);
        const mandatoryEmpty = properties.filter(p =>
            isMandatoryProp(p, properties) &&
            (p.value === undefined || p.value === null || p.value === '')
        );
        if (mandatoryEmpty.length > 0) {
            setTouchedFields(prev => {
                const next = new Set(prev);
                mandatoryEmpty.forEach(p => next.add(p.name));
                return next;
            });
        }
    }, []);

    const handlePropertyChange = (index, value) => {
        var updatedProperties = [...properties];
        if (Array.isArray(updatedProperties[index].value)) {
            if (updatedProperties[index].value.includes(value)) {
                updatedProperties[index].value = updatedProperties[index].value.filter(v => v !== value);
            } else {
                updatedProperties[index].value = [...updatedProperties[index].value, value];
            }
        } else {
            updatedProperties[index].value = value;
        }
        setProperties(updatedProperties);
        validate(updatedProperties);
    };

    const mandatoryProps = properties
        .map((p, i) => ({ ...p, _origIndex: i }))
        .filter(p => isMandatoryProp(p, properties));
    const optionalProps = properties
        .map((p, i) => ({ ...p, _origIndex: i }))
        .filter(p => !isMandatoryProp(p, properties));

    const totalRequired = mandatoryProps.length;
    const filledRequired = mandatoryProps.filter(p => {
        const isEmpty = p.value === undefined || p.value === null || p.value === "" ||
            (Array.isArray(p.value) && p.value.length === 0);
        return !isEmpty;
    }).length;
    const progressPercent = totalRequired > 0 ? (filledRequired / totalRequired) * 100 : 100;

    const getNumberAdornment = (property) => {
        const ft = (property.fieldType || '').toLowerCase();
        const showPercentage = property.showCurrencySymbol === false || ft === 'percentage' || ft === 'percent';
        if (showPercentage || ft.includes('percent')) {
            return { endAdornment: <InputAdornment position="end">%</InputAdornment> };
        }
        if (property.showCurrencySymbol || ft === 'currency' || ft.includes('currency')) {
            return { startAdornment: <InputAdornment position="start">{property.currencySymbol || '$'}</InputAdornment> };
        }
        return {};
    };

    const getTextInputProps = (property) => {
        const ft = (property.fieldType || '').toLowerCase();
        if (ft === 'phone_number' || ft === 'phonenumber' || ft === 'phone') {
            return { type: 'tel', inputMode: 'tel', autoComplete: 'tel' };
        }
        if (ft === 'email') {
            return { type: 'email', inputMode: 'email', autoComplete: 'email' };
        }
        if (ft === 'url' || ft === 'website') {
            return { type: 'url', inputMode: 'url' };
        }
        return { type: 'text' };
    };

    const renderPropertyInput = (property, origIndex) => {
        const hasValidationError = errors[property.name];
        const hasError = hasValidationError && touchedFields.has(property.name);
        const isRequired = isMandatoryProp(property, properties);
        const requiredHelperText = isRequired ? t('required') : '';
        const visibleOptions = Array.isArray(property.options)
            ? property.options.filter((option) => !option?.hidden)
            : [];

        if ((property.type === 'bool' || property.fieldType === 'radio') && visibleOptions.length > 0) {
            return (
                <Stack spacing={1}>
                    <Typography sx={{ color: hasError ? STATUS_LOST : TEXT_PRIMARY, fontWeight: isRequired ? 600 : 400 }}>
                        {property.label} {isRequired && <Typography component="span" sx={{ color: ACCENT, fontWeight: 700 }}>*</Typography>}
                    </Typography>
                    <RadioGroup
                        value={property.value?.toString()}
                        onChange={(event) => { markTouched(property.name); handlePropertyChange(origIndex, event.target.value); }}
                    >
                        {visibleOptions.map((option, optionIndex) => (
                            <FormControlLabel
                                key={optionIndex}
                                value={option.value}
                                control={<Radio sx={{
                                    color: hasError ? STATUS_LOST : TEXT_SECONDARY,
                                    '&.Mui-checked': { color: ACCENT },
                                    padding: '12px',
                                }} />}
                                label={<Typography variant="body2" sx={{ color: TEXT_PRIMARY }}>{option.label}</Typography>}
                                sx={{ minHeight: 48 }}
                            />
                        ))}
                    </RadioGroup>
                </Stack>
            );
        }

        if (property.type === 'enumeration' && visibleOptions.length > 0) {
            let value = property.value ? property.value : '';
            if (property.fieldType === 'checkbox') {
                property.value = Array.isArray(value) ? value.map(v => v.trim()) : value.split(';').map(v => v.trim());
            }

            // Searchable select for >8 options
            if (property.fieldType === 'select' && visibleOptions.length > 8) {
                return (
                    <Autocomplete
                        options={visibleOptions}
                        getOptionLabel={(opt) => opt.label || ''}
                        value={visibleOptions.find(o => o.value === property.value) || null}
                        onChange={(e, newVal) => {
                            markTouched(property.name);
                            handlePropertyChange(origIndex, newVal ? newVal.value : '');
                        }}
                        renderInput={(params) => (
                            <TextField {...params}
                                label={`${property.label}${isRequired ? " *" : ""}`}
                                error={hasError}
                                helperText={hasError ? (t('mandatoryField') || t('required')) : property.description}
                            />
                        )}
                        slotProps={{ paper: { sx: { '& .MuiAutocomplete-option': { minHeight: 48 } } } }}
                    />
                );
            }

            if (property.fieldType === 'checkbox') {
                return (
                    <Stack spacing={1}>
                        <Typography sx={{ color: hasError ? STATUS_LOST : TEXT_PRIMARY, fontWeight: isRequired ? 600 : 400 }}>
                            {property.label} {isRequired && <Typography component="span" sx={{ color: ACCENT, fontWeight: 700 }}>*</Typography>}
                        </Typography>
                        <FormGroup>
                            {visibleOptions.map((option, optionIndex) => (
                                <FormControlLabel
                                    key={optionIndex}
                                    control={
                                        <Checkbox
                                            checked={property.value.includes(option.value)}
                                            onChange={() => { markTouched(property.name); handlePropertyChange(origIndex, option.value); }}
                                            sx={{
                                                color: hasError ? STATUS_LOST : TEXT_SECONDARY,
                                                '&.Mui-checked': { color: ACCENT },
                                                padding: '12px',
                                            }}
                                        />
                                    }
                                    label={<Typography variant="body2" sx={{ color: TEXT_PRIMARY }}>{option.label}</Typography>}
                                    sx={{ minHeight: 48 }}
                                />
                            ))}
                        </FormGroup>
                    </Stack>
                );
            } else {
                return (
                    <Stack spacing={1}>
                        <Typography sx={{ color: hasError ? STATUS_LOST : TEXT_PRIMARY, fontWeight: isRequired ? 600 : 400 }}>
                            {property.label} {isRequired && <Typography component="span" sx={{ color: ACCENT, fontWeight: 700 }}>*</Typography>}
                        </Typography>
                        <Select
                            error={hasError}
                            value={property.value || ''}
                            onChange={(event) => { markTouched(property.name); handlePropertyChange(origIndex, event.target.value); }}
                            onClose={() => markTouched(property.name)}
                            sx={{
                                minHeight: 48,
                                '.MuiOutlinedInput-notchedOutline': {
                                    borderColor: hasError ? STATUS_LOST : 'rgba(255,255,255,0.3)',
                                },
                            }}
                        >
                            {visibleOptions.map((option, optionIndex) => (
                                <MenuItem key={optionIndex} value={option.value} sx={{ minHeight: 48, color: TEXT_PRIMARY }}>{option.label}</MenuItem>
                            ))}
                        </Select>
                    </Stack>
                );
            }
        }

        if (property.type === 'date' || property.type === 'dateTime') {
            return (
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                    {property.type === 'date' ? (
                        <MobileDatePicker
                            label={`${property.label}${isRequired ? " *" : ""}`}
                            value={property.value ? dayjs(property.value) : null}
                            slotProps={{
                                textField: {
                                    error: hasError,
                                    fullWidth: true,
                                    helperText: hasError ? requiredHelperText : property.description,
                                    onBlur: () => markTouched(property.name),
                                }
                            }}
                            onClose={() => markTouched(property.name)}
                            onChange={(event) => {
                                markTouched(property.name);
                                handlePropertyChange(origIndex, event ? event.format("YYYY-MM-DD") : "");
                            }}
                        />
                    ) : (
                        <MobileDateTimePicker
                            label={`${property.label}${isRequired ? " *" : ""}`}
                            value={property.value ? dayjs(property.value) : null}
                            slotProps={{
                                textField: {
                                    error: hasError,
                                    fullWidth: true,
                                    helperText: hasError ? requiredHelperText : property.description,
                                    onBlur: () => markTouched(property.name),
                                }
                            }}
                            onClose={() => markTouched(property.name)}
                            onChange={(event) => {
                                markTouched(property.name);
                                handlePropertyChange(origIndex, event ? event.format("YYYY-MM-DDTHH:mm:ss.SSS[Z]") : "");
                            }}
                        />
                    )}
                </LocalizationProvider>
            );
        }

        if (property.type === 'number') {
            const adornmentProps = getNumberAdornment(property);
            return (
                <TextField
                    error={hasError}
                    fullWidth
                    type="number"
                    inputMode="decimal"
                    label={`${property.label}${isRequired ? " *" : ""}`}
                    value={property.value || ''}
                    onChange={(event) => handlePropertyChange(origIndex, event.target.value)}
                    onBlur={() => markTouched(property.name)}
                    helperText={hasError ? requiredHelperText : property.description}
                    InputProps={adornmentProps}
                    slotProps={{ htmlInput: { inputMode: 'decimal' } }}
                />
            );
        }

        const textProps = getTextInputProps(property);
        return (
            <TextField
                error={hasError}
                fullWidth
                type={textProps.type}
                label={`${property.label}${isRequired ? " *" : ""}`}
                value={property.value || ''}
                onChange={(event) => handlePropertyChange(origIndex, event.target.value)}
                onBlur={() => markTouched(property.name)}
                helperText={hasError ? requiredHelperText : property.description}
                multiline={property.type === 'string' && property.multiline}
                slotProps={{ htmlInput: { inputMode: textProps.inputMode, autoComplete: textProps.autoComplete } }}
                InputProps={property.type === 'string' ? {
                    endAdornment: (
                        <VoiceInput
                            value={property.value || ''}
                            onResult={(text) => handlePropertyChange(origIndex, text)}
                        />
                    )
                } : undefined}
            />
        );
    };

    return (
        <Stack spacing={2} style={{ width: "100%" }}>
            {totalRequired > 0 && (
                <Box sx={{ width: '100%' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
                        <Typography variant="caption" sx={{ color: TEXT_SECONDARY }}>
                            {t('requiredFields') || 'Required fields'}
                        </Typography>
                        <Typography variant="caption" sx={{ color: filledRequired === totalRequired ? ACCENT : TEXT_SECONDARY, fontWeight: 600 }}>
                            {filledRequired}/{totalRequired}
                        </Typography>
                    </Stack>
                    <LinearProgress
                        variant="determinate"
                        value={progressPercent}
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: 'rgba(255,255,255,0.12)',
                            '& .MuiLinearProgress-bar': {
                                borderRadius: 3,
                                backgroundColor: filledRequired === 0 ? STATUS_LOST
                                    : filledRequired < totalRequired ? '#FFB74D'
                                    : ACCENT,
                            }
                        }}
                    />
                </Box>
            )}

            {mandatoryProps.map((property) => (
                <div key={property._origIndex}>
                    {renderPropertyInput(property, property._origIndex)}
                </div>
            ))}

            {optionalProps.length > 0 && mandatoryProps.length > 0 && (
                <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', pt: 1 }}>
                    <Typography variant="caption" sx={{ color: TEXT_TERTIARY, px: 1 }}>
                        {t('optionalFields')}
                    </Typography>
                </Divider>
            )}

            {optionalProps.map((property) => (
                <div key={property._origIndex}>
                    {renderPropertyInput(property, property._origIndex)}
                </div>
            ))}
        </Stack>
    );
};

export default Properties;
